import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';

/** Replaces one eligible line through one Shopify update, using real variant IDs. */
class EvenmornUpgrade extends Component {
  static #busy = false;
  #request;
  disconnectedCallback() { super.disconnectedCallback(); this.#request?.abort(); }
  /** @param {MouseEvent} event */
  async upgrade(event) {
    const link = event.target;
    if (!(link instanceof HTMLAnchorElement) || event.metaKey || event.ctrlKey) return;
    event.preventDefault();
    if (EvenmornUpgrade.#busy) return;
    const cartComponent = this.closest('cart-items-component');
    if (cartComponent?.classList.contains('cart-items-disabled')) return;
    EvenmornUpgrade.#busy = true;
    this.#request = new AbortController();
    const { signal } = this.#request;
    const root = this.dataset.root || '/';
    const status = this.querySelector('[data-upgrade-status]');
    const deferred = CartLinesUpdateEvent.createPromise();
    const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean))];
    cartComponent?.classList.add('cart-items-disabled');
    link.setAttribute('aria-disabled', 'true');
    let dispatched = false;
    let renderResult;
    try {
      const snapshot = await fetch(new URL(`${root}cart.js`, location.origin), { signal });
      if (!snapshot.ok) throw new Error(this.dataset.error);
      const before = await snapshot.json();
      const source = before.items.find((item) => item.key === link.dataset.sourceKey);
      if (!source || source.product_id !== Number(this.dataset.productId) || source.quantity !== 1 || source.final_price !== Number(link.dataset.sourcePrice) || source.selling_plan_allocation || Object.keys(source.properties || {}).length || before.items.some((item) => item.variant_id === Number(link.dataset.targetVariant))) throw new Error(this.dataset.changed);
      // Recheck the target against live catalog data before altering the cart.
      const catalogResponse = await fetch(new URL(`${this.dataset.productUrl}.js`, location.origin), { signal });
      if (!catalogResponse.ok) throw new Error(this.dataset.error);
      const catalog = await catalogResponse.json();
      const sourceVariant = catalog.variants.find((variant) => variant.id === source.variant_id);
      const target = catalog.variants.find((variant) => variant.id === Number(link.dataset.targetVariant));
      const packIndex = catalog.options.findIndex((option) => (typeof option === 'string' ? option : option.name)?.toLowerCase() === 'pack');
      const optionsMatch = packIndex >= 0 && Array.isArray(sourceVariant?.options) && Array.isArray(target?.options)
        && sourceVariant.options.length === target.options.length
        && sourceVariant.options.every((value, index) => index === packIndex || value === target.options[index]);
      if (catalog.id !== source.product_id || !optionsMatch || !target.available || target.price !== Number(link.dataset.targetPrice)) throw new Error(this.dataset.changed);
      this.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [{ id: source.key, quantity: 0 }], promise: deferred.promise }));
      dispatched = true;
      const response = await fetch(new URL(`${root}cart/update.js`, location.origin), {
        method: 'POST', signal, headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: { [source.key]: 0, [target.id]: 1 }, sections, sections_url: location.pathname }),
      });
      const cart = await response.json();
      if (!response.ok || cart.errors || cart.status) throw new Error(typeof cart.description === 'string' ? cart.description : this.dataset.error);
      renderResult = cart;
      // Existing cart component consumes this standard result and owns announcements of totals.
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { sections: cart.sections, items: cart.items, source: 'evenmorn-upgrade', didError: false } });
      if (status) status.textContent = this.dataset.success;
    } catch (error) {
      if (dispatched) deferred.reject(error);
      if (error.name !== 'AbortError' && status) status.textContent = error.message || this.dataset.error;
    } finally {
      EvenmornUpgrade.#busy = false;
      cartComponent?.classList.remove('cart-items-disabled');
      link.removeAttribute('aria-disabled');
    }
    // Bundled sections may be null after a successful mutation: refresh the full cart.
    if (renderResult && sections.some((id) => !renderResult.sections?.[id])) location.assign(new URL(`${root}cart`, location.origin));
  }
}
if (!customElements.get('evenmorn-upgrade-component')) customElements.define('evenmorn-upgrade-component', EvenmornUpgrade);
